// Sending the offline queue, and one shared view of where it stands.
//
// The logger's header ("3 to sync") and the chip on every other screen
// (SyncStatus) read the same store, so they can never disagree about how many
// sets are waiting. Same pattern as updatePrompt.js: a tiny module store, not
// context, because a drain can finish after the screen that started it unmounts.

import axios from 'axios';
import { buildApiUrl } from '../../config/api';
import { drain, pendingFor, failedFor, dismiss as dismissItem } from './offlineQueue';

const EMPTY = { user: null, pending: [], failed: [], needsSignIn: false, syncing: false };
let state = EMPTY;
const listeners = new Set();

function set(next) {
  state = { ...state, ...next };
  listeners.forEach((listener) => listener(state));
}

export function getSyncState() {
  return state;
}

export function subscribeSync(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Re-read the queue for this user. Call after enqueueing. */
export function refreshSync(user) {
  set({
    user,
    pending: user ? pendingFor(user) : [],
    failed: user ? failedFor(user) : [],
  });
}

/** Test-only: forget everything between tests. */
export function resetSyncStore() {
  state = EMPTY;
  listeners.clear();
  inFlight = null;
  again = null;
}

function axiosSend(token) {
  return (item) => axios.post(buildApiUrl(item.path), item.body, {
    headers: { Authorization: `Bearer ${token}` },
  });
}

let inFlight = null;
let again = null;

/**
 * Send this user's waiting sets.
 *
 * Single-flight: two drains at once would send the same sets twice. (The
 * server's clientId makes that harmless, but it doubles the traffic on the
 * worst network the app sees.)
 *
 * But a call that arrives DURING a drain is not simply joined to it. The
 * `online` event lands exactly then: the drain started offline, is failing,
 * and the network has just come back. Joining would hand the event a drain that
 * is about to report "still offline", and the sets would wait for the next
 * timer. Instead one more drain is run as soon as the current one finishes.
 */
export function runSync({ user, token, send = token ? axiosSend(token) : null }) {
  if (!user || !send) return Promise.resolve(null);
  if (inFlight) {
    again = { user, send };
    return inFlight;
  }

  set({ syncing: true });
  inFlight = drain({ user, send })
    .then((summary) => {
      set({ needsSignIn: summary.stoppedBy === 'auth' });
      return summary;
    })
    .finally(() => {
      inFlight = null;
      refreshSync(user);
      set({ syncing: false });
      const next = again;
      again = null;
      if (next) runSync(next);
    });
  return inFlight;
}

export function dismissFailed(user, id) {
  dismissItem(id);
  refreshSync(user);
}
